<template>
  <div class="sinir-credentials-container">
    <b-card class="mb-4">
      <template #header>
        <div class="d-flex align-items-center">
          <feather-icon
            icon="LinkIcon"
            size="18"
            class="mr-2"
          />
          <h5 class="mb-0">
            Credenciais de Integração SINIR
          </h5>
        </div>
      </template>

      <b-alert
        v-if="successMessage"
        show
        variant="success"
        class="mb-3"
      >
        {{ successMessage }}
      </b-alert>

      <b-alert
        v-if="errorMessage"
        show
        variant="danger"
        class="mb-3"
      >
        {{ errorMessage }}
      </b-alert>

      <p class="text-muted mb-4">
        Configure suas credenciais do SINIR para gerar MTRs automaticamente nas operações de resíduos.
      </p>

      <b-form @submit.prevent="saveCredentials">
        <b-row>
          <b-col md="6">
            <b-form-group
              label="CPF/CNPJ do Gerador (SINIR)"
              label-for="sinir-cpf-cnpj"
            >
              <b-form-input
                id="sinir-cpf-cnpj"
                v-model="credentials.sinir_cpf_cnpj"
                type="text"
                placeholder="Apenas números (ex: 12345678901)"
              />
              <small class="text-muted">CPF ou CNPJ cadastrado no sistema SINIR</small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Senha SINIR"
              label-for="sinir-senha"
            >
              <b-input-group>
                <b-form-input
                  id="sinir-senha"
                  v-model="credentials.sinir_senha"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="hasSenha ? '••••••••' : 'Sua senha do SINIR'"
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    @click="showPassword = !showPassword"
                  >
                    <feather-icon :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <small
                v-if="hasSenha"
                class="text-success"
              >
                ✓ Senha já configurada. Deixe em branco para manter a atual.
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Código da Unidade SINIR"
              label-for="sinir-unidade"
            >
              <b-form-input
                id="sinir-unidade"
                v-model.number="credentials.sinir_unidade"
                type="number"
                placeholder="Ex: 123456"
              />
              <small class="text-muted">Código do empreendimento cadastrado no SINIR</small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Nome do Responsável"
              label-for="sinir-responsavel"
            >
              <b-form-input
                id="sinir-responsavel"
                v-model="credentials.sinir_nome_responsavel"
                type="text"
                placeholder="Nome completo do responsável"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <hr class="my-3">

        <div class="d-flex justify-content-end">
          <b-button
            variant="primary"
            type="submit"
            :disabled="isSaving"
            class="btn-grad"
          >
            <b-spinner
              v-if="isSaving"
              small
              class="mr-1"
            />
            {{ isSaving ? 'Salvando...' : 'Salvar Credenciais' }}
          </b-button>
        </div>
      </b-form>
    </b-card>

    <b-card class="bg-light-info">
      <h6 class="mb-2">
        <feather-icon
          icon="InfoIcon"
          size="16"
          class="mr-1"
        />
        Sobre a Integração SINIR
      </h6>
      <p class="mb-2 text-muted small">
        O SINIR (Sistema Nacional de Informações sobre a Gestão de Resíduos Sólidos) é o sistema
        oficial do Ministério do Meio Ambiente para gerenciamento de Manifestos de Transporte de
        Resíduos (MTR).
      </p>
      <p class="mb-0 text-muted small">
        <strong>Como obter credenciais:</strong> Acesse
        <a
          href="https://mtr.sinir.gov.br"
          target="_blank"
        >mtr.sinir.gov.br</a>,
        crie sua conta e cadastre seu empreendimento.
      </p>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BRow,
  BCol,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import api from '@/libs/axios'

export default {
  name: 'MyAccountSinir',
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BRow,
    BCol,
    BAlert,
    BSpinner,
  },
  data() {
    return {
      credentials: {
        sinir_cpf_cnpj: '',
        sinir_senha: '',
        sinir_unidade: null,
        sinir_nome_responsavel: '',
      },
      hasSenha: false,
      showPassword: false,
      isSaving: false,
      successMessage: '',
      errorMessage: '',
    }
  },
  mounted() {
    this.loadCredentials()
  },
  methods: {
    async loadCredentials() {
      try {
        const response = await api.get('/system/users/me/sinir')
        this.credentials.sinir_cpf_cnpj = response.data.sinir_cpf_cnpj || ''
        this.credentials.sinir_unidade = response.data.sinir_unidade || null
        this.credentials.sinir_nome_responsavel = response.data.sinir_nome_responsavel || ''
        this.hasSenha = response.data.has_senha || false
      } catch (error) {
        console.error('Erro ao carregar credenciais SINIR:', error)
      }
    },
    async saveCredentials() {
      this.isSaving = true
      this.successMessage = ''
      this.errorMessage = ''

      try {
        const payload = { ...this.credentials }
        // Se a senha estiver vazia, não enviar no payload
        if (!payload.sinir_senha) {
          delete payload.sinir_senha
        }

        await api.patch('/system/users/me/sinir', payload)

        this.successMessage = 'Credenciais salvas com sucesso!'
        this.hasSenha = !!this.credentials.sinir_senha || this.hasSenha
        this.credentials.sinir_senha = '' // Limpar campo senha após salvar

        setTimeout(() => {
          this.successMessage = ''
        }, 5000)
      } catch (error) {
        console.error('Erro ao salvar credenciais SINIR:', error)
        this.errorMessage = error.response?.data?.message || 'Erro ao salvar credenciais'
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style scoped>
.sinir-credentials-container {
  max-width: 900px;
}

.bg-light-info {
  background-color: rgba(0, 123, 255, 0.08);
  border: 1px solid rgba(0, 123, 255, 0.2);
}
</style>
